import ConfirmModal from "@/src/components/action-modals/confirm-modal";
import ShareModal from "@/src/components/action-modals/share-modal";
import DropDown from "@/src/components/ui-kit/drop-down";
import BlocksAvatar from "@/src/components/ui-kit/blocks-avatar";
import {
  formatArticleDate,
  resolveAuthorName,
} from "@/src/hooks/features/news/use-latest-news";
import { usePermissions } from "@/src/hooks/use-permissions";
import { BlocksApiNews } from "@/src/models/news.model";
import { useTheme } from "@/src/styles/theme-provider";
import { PictureFrame } from "iconsax-react-nativejs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Image,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
} from "react-native";

export type NewsCardVariant = "all" | "saved" | "draft" | "pinned";

type AllNewsCardProps = Readonly<{
  newsItem: BlocksApiNews;
  categoryNames: string[];
  isSaved: boolean;
  isPinned: boolean;
  isOwner: boolean;
  /** Pinned-tab-only "New" badge — true when this article is not yet in the
   * viewer's `newsRead`. Undefined/omitted elsewhere, so no other variant
   * renders it. */
  isNew?: boolean;
  variant?: NewsCardVariant;
  onPress: () => void;
  onSave: () => void;
  onEdit: () => void;
  onPin: () => void;
  onDelete: () => void;
  itemWidthClass: string;
}>;

export function AllNewsCard({
  newsItem,
  categoryNames,
  isSaved,
  isPinned,
  isOwner,
  isNew = false,
  variant = "all",
  onPress,
  onSave,
  onEdit,
  onPin,
  onDelete,
  itemWidthClass,
}: AllNewsCardProps) {
  const { t } = useTranslation();
  const { themeVars } = useTheme();
  const { canUpdate, canDelete, isSuperAdmin } = usePermissions();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [isCardHovered, setIsCardHovered] = useState(false);
  const [isCardPressed, setIsCardPressed] = useState(false);

  const isWeb = Platform.OS === "web";
  const isHighlighted = isWeb ? isCardHovered : isCardPressed;

  const isDraft = variant === "draft";

  const authorName = resolveAuthorName(newsItem);
  const formattedDate = formatArticleDate(newsItem.CreatedDate);

  const editItems = [
    ...(canUpdate("news") ? [{ label: t("BTN_EDIT"), onPress: onEdit }] : []),
    ...(canDelete("news")
      ? [
          {
            label: t("BTN_DELETE"),
            onPress: () => setShowDeleteConfirm(true),
            destructive: true,
          },
        ]
      : []),
  ];

  const dropdownItems = isDraft
    ? editItems
    : [
        { label: isSaved ? t("BTN_UNSAVE") : t("BTN_SAVE"), onPress: onSave },
        { label: t("LABEL_SHARE"), onPress: () => setShowShareModal(true) },
        ...(canUpdate("news")
          ? [
              {
                label: isPinned ? t("BTN_UNPIN") : t("BTN_PIN"),
                onPress: onPin,
              },
            ]
          : []),
        ...(isOwner || isSuperAdmin ? editItems : []),
      ];

  const confirmDelete = () => {
    onDelete();
    setShowDeleteConfirm(false);
  };

  return (
    <>
      <ConfirmModal
        visible={showDeleteConfirm}
        title="BTN_DELETE"
        description={
          isDraft ? "LABEL_DESC_DELETE_DRAFT" : "LABEL_DESC_DELETE_NEWS"
        }
        confirmText={t("BTN_DELETE")}
        cancelText={t("BTN_CANCEL")}
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      <ShareModal
        visible={showShareModal}
        onClose={() => setShowShareModal(false)}
        resourceId={newsItem.ItemId}
        resource="news"
        title={newsItem.Title}
      />

      <Pressable
        onPress={onPress}
        className={`${itemWidthClass} bg-chalk rounded-xl md:rounded-2xl md:border md:mb-4 p-2 gap-2`}
        onHoverIn={() => setIsCardHovered(true)}
        onHoverOut={() => setIsCardHovered(false)}
        onPressIn={() => !isWeb && setIsCardPressed(true)}
        onPressOut={() => !isWeb && setIsCardPressed(false)}
        style={{
          borderColor: isHighlighted
            ? (themeVars["--color-primary-default"] as string)
            : (themeVars["--color-ash-3"] as string),
        }}
      >
        <View className="h-32 items-center justify-center overflow-hidden rounded-lg relative">
          {newsItem.coverImageUrl?.trim() ? (
            <Image
              className="w-full h-full rounded-lg"
              source={{ uri: newsItem.coverImageUrl }}
              resizeMode="cover"
            />
          ) : (
            <View className="flex-1 w-full rounded-lg bg-obsidian items-center justify-center">
              <PictureFrame
                size={30}
                color={themeVars["--color-ash-1"] as string}
              />
            </View>
          )}

          {isNew && (
            <View className="absolute top-2 left-2 bg-primary rounded-full px-2 py-0.5">
              <Text className="text-white text-[10px] font-bold">
                {t("LABEL_NEW")}
              </Text>
            </View>
          )}
        </View>

        {/* Author row */}
        <View className="flex-row justify-between items-center px-1">
          <View className="flex-row gap-2 flex-1">
            <BlocksAvatar
              userId={newsItem.CreatedBy}
              fallbackImageUrl={newsItem.authorSnapshot?.profileImageUrl}
              size={30}
              className="rounded-full"
            />
            <View className="flex-col flex-1">
              <Text className="font-medium text-sm text-onyx" numberOfLines={1}>
                {authorName}
              </Text>
            </View>
          </View>

          <TouchableOpacity
            activeOpacity={1}
            onPress={(event) => event.stopPropagation()}
            className="justify-center"
          >
            <DropDown items={dropdownItems} />
          </TouchableOpacity>
        </View>

        <View className="px-1">
          <Text className="font-semibold text-sm text-onyx" numberOfLines={2}>
            {newsItem.Title ?? (
              <Text className="italic text-ash-1">{t("LABEL_NO_TITLE")}</Text>
            )}
          </Text>
        </View>

        <View className="flex-row gap-2 items-center px-1">
          {formattedDate && (
            <Text className="text-xs text-ash-1">{formattedDate}</Text>
          )}
          {!isDraft && categoryNames.length > 0 && (
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={{ gap: 8 }}
              className="flex-1"
            >
              {categoryNames.map((name) => (
                <Text key={name} className="text-xs text-primary font-semibold">
                  {name}
                </Text>
              ))}
            </ScrollView>
          )}
        </View>
      </Pressable>
    </>
  );
}
